import React, { useState, useCallback, useMemo } from 'react';
import { Settings, Plus, Pencil, Check, X, Loader2, Trash2, Tag, ChevronDown } from 'lucide-react';
import type {
  AppSettingVarType,
  AppSettingListItemType,
  TagType,
  EventTypeDefaultTag,
} from '@peles/shared';
import {
  useSettingsEventTypes,
  useCreateEventType,
  useUpdateEventType,
  useSettingsVendorCategories,
  useCreateVendorCategory,
  useUpdateVendorCategory,
  useDeleteVendorCategory,
  useSettingsExpenseStatuses,
  useCreateExpenseStatus,
  useUpdateExpenseStatus,
  useSettingsPaymentMethods,
  useCreatePaymentMethod,
  useUpdatePaymentMethod,
  useSettingsCatalogItems,
  useCreateSettingsCatalogItem,
  useUpdateSettingsCatalogItem,
  useCatalogItemUsage,
  useDeleteSettingsCatalogItem,
  useSettingsFloorPlanObjectTypes,
  useCreateFloorPlanObjectType,
  useUpdateFloorPlanObjectType,
  useDeleteFloorPlanObjectType,
  useEventTypeDefaultTags,
  useCreateEventTypeDefaultTag,
  useUpdateEventTypeDefaultTag,
  useDeleteEventTypeDefaultTag,
} from '../hooks/useSettings';
import { useSettingsVendorCategories as useVendorCatsForCatalog } from '../hooks/useSettings';
import {
  usePlanningStatuses,
  useCreatePlanningStatus,
  useUpdatePlanningStatus,
} from '../hooks/usePlanning';
import { SettingToggle } from '../components/settings/SettingToggle';
import { FilterSelect } from '../components/common';
import { SearchableListSelect } from '../components/common/SearchableListSelect';
import { ReassignDeleteModal } from '../components/common/ReassignDeleteModal';
import { useDuplicateNameGuard, isSameName } from '../hooks/useDuplicateNameGuard';
import {
  useAppSettings,
  useCreateAppSetting,
  useUpdateAppSetting,
  useDeleteAppSetting,
  useAppSettingLists,
  useCreateAppSettingList,
  useUpdateAppSettingList,
  useDeleteAppSettingList,
} from '../hooks/useAppSettings';
import { SearchableSelect } from '../components/common/SearchableSelect';
import { AiModelsSection } from '../components/settings/AiModelsSection';
import { useDialog } from '../context/DialogContext';

type TabId =
  | 'eventTypes'
  | 'vendorCategories'
  | 'expenseStatuses'
  | 'planningStatuses'
  | 'paymentMethods'
  | 'catalogItems'
  | 'floorPlanObjects'
  | 'appSettings';

const TABS: { id: TabId; label: string }[] = [
  { id: 'eventTypes', label: 'Event Types' },
  { id: 'vendorCategories', label: 'Vendor Categories' },
  { id: 'expenseStatuses', label: 'Expense Statuses' },
  { id: 'planningStatuses', label: 'Planning Statuses' },
  { id: 'paymentMethods', label: 'Payment Methods' },
  { id: 'catalogItems', label: 'Catalog Items' },
  { id: 'floorPlanObjects', label: 'Floor Plan Objects' },
  { id: 'appSettings', label: 'App Settings' },
];

export function SettingsPage() {
  const [activeTab, setActiveTab] = useState<TabId>('eventTypes');

  return (
    <div>
      <div className="flex items-center gap-2 mb-6">
        <Settings size={24} className="text-primary-600" />
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-4 -mb-px" role="tablist">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        {activeTab === 'eventTypes' && <EventTypesTab />}
        {activeTab === 'vendorCategories' && <VendorCategoriesTab />}
        {activeTab === 'expenseStatuses' && <ExpenseStatusesTab />}
        {activeTab === 'planningStatuses' && <PlanningStatusesTab />}
        {activeTab === 'paymentMethods' && <PaymentMethodsTab />}
        {activeTab === 'catalogItems' && <CatalogItemsTab />}
        {activeTab === 'floorPlanObjects' && <FloorPlanObjectsTab />}
        {activeTab === 'appSettings' && <AppSettingsTab />}
      </div>
    </div>
  );
}

// ─── Event Types Tab ─────────────────────────────────────────────────────────

export function EventTypesTab() {
  const { data: items = [], isLoading } = useSettingsEventTypes();
  const createMutation = useCreateEventType();
  const updateMutation = useUpdateEventType();
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', nameHe: '', description: '' });
  const [expandedTagsId, setExpandedTagsId] = useState<string | null>(null);

  const handleAdd = useCallback(() => {
    setAdding(true);
    setEditingId(null);
    setForm({ name: '', nameHe: '', description: '' });
  }, []);

  const handleEdit = useCallback(
    (item: { id: string; name: string; nameHe: string | null; description: string | null }) => {
      setEditingId(item.id);
      setAdding(false);
      setForm({
        name: item.name,
        nameHe: item.nameHe || '',
        description: item.description || '',
      });
    },
    [],
  );

  const handleSaveNew = useCallback(async () => {
    await createMutation.mutateAsync({
      name: form.name,
      ...(form.nameHe && { nameHe: form.nameHe }),
      ...(form.description && { description: form.description }),
    });
    setAdding(false);
    setForm({ name: '', nameHe: '', description: '' });
  }, [createMutation, form]);

  const handleSaveEdit = useCallback(async () => {
    if (!editingId) return;
    await updateMutation.mutateAsync({
      id: editingId,
      updates: {
        name: form.name,
        nameHe: form.nameHe || null,
        description: form.description || null,
      },
    });
    setEditingId(null);
  }, [editingId, updateMutation, form]);

  const handleToggleActive = useCallback(
    async (id: string, currentActive: boolean) => {
      await updateMutation.mutateAsync({ id, updates: { isActive: !currentActive } });
    },
    [updateMutation],
  );

  const handleCancel = useCallback(() => {
    setAdding(false);
    setEditingId(null);
    setForm({ name: '', nameHe: '', description: '' });
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 size={24} className="animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Event Types</h2>
        <button
          onClick={handleAdd}
          disabled={adding}
          className="flex items-center gap-1 px-3 py-1.5 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
        >
          <Plus size={14} />
          Add
        </button>
      </div>

      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Name
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Name (Hebrew)
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Description
            </th>
            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
              Active
            </th>
            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
              Actions
            </th>
          </tr>
        </thead>
        {/* Default tags expansion rows are rendered inside tbody below */}
        <tbody>
          {adding && (
            <tr className="border-b border-gray-100 bg-blue-50">
              <td className="px-4 py-2">
                <input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Name"
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  data-testid="new-name-input"
                />
              </td>
              <td className="px-4 py-2">
                <input
                  value={form.nameHe}
                  onChange={(e) => setForm((f) => ({ ...f, nameHe: e.target.value }))}
                  placeholder="Name (Hebrew)"
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                />
              </td>
              <td className="px-4 py-2">
                <input
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Description"
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                />
              </td>
              <td />
              <td className="px-4 py-2 text-right">
                <button
                  onClick={handleSaveNew}
                  disabled={!form.name.trim() || createMutation.isPending}
                  className="text-green-600 hover:text-green-800 p-1 disabled:opacity-50"
                  aria-label="Save new item"
                >
                  <Check size={16} />
                </button>
                <button
                  onClick={handleCancel}
                  className="text-gray-400 hover:text-gray-600 p-1 ml-1"
                  aria-label="Cancel"
                >
                  <X size={16} />
                </button>
              </td>
            </tr>
          )}
          {items.map((item) => (
            <React.Fragment key={item.id}>
              <tr className="border-b border-gray-100">
                {editingId === item.id ? (
                  <>
                    <td className="px-4 py-2">
                      <input
                        value={form.name}
                        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                        data-testid="edit-name-input"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        value={form.nameHe}
                        onChange={(e) => setForm((f) => ({ ...f, nameHe: e.target.value }))}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        value={form.description}
                        onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                      />
                    </td>
                    <td />
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={handleSaveEdit}
                        disabled={!form.name.trim() || updateMutation.isPending}
                        className="text-green-600 hover:text-green-800 p-1 disabled:opacity-50"
                        aria-label="Save edit"
                      >
                        <Check size={16} />
                      </button>
                      <button
                        onClick={handleCancel}
                        className="text-gray-400 hover:text-gray-600 p-1 ml-1"
                        aria-label="Cancel"
                      >
                        <X size={16} />
                      </button>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-4 py-2 text-gray-900">{item.name}</td>
                    <td className="px-4 py-2 text-gray-600">{item.nameHe || '-'}</td>
                    <td className="px-4 py-2 text-gray-600">{item.description || '-'}</td>
                    <td className="px-4 py-2 text-center">
                      <button
                        onClick={() => handleToggleActive(item.id, item.isActive)}
                        className={`inline-block w-8 h-4 rounded-full relative transition-colors ${
                          item.isActive ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                        aria-label={item.isActive ? 'Deactivate' : 'Activate'}
                      >
                        <span
                          className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-transform ${
                            item.isActive ? 'left-4' : 'left-0.5'
                          }`}
                        />
                      </button>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={() =>
                          setExpandedTagsId(expandedTagsId === item.id ? null : item.id)
                        }
                        className="text-gray-400 hover:text-primary-600 p-1"
                        aria-label={`${expandedTagsId === item.id ? 'Hide' : 'Show'} default tags`}
                        data-testid={`toggle-tags-${item.id}`}
                      >
                        {expandedTagsId === item.id ? <ChevronDown size={14} /> : <Tag size={14} />}
                      </button>
                      <button
                        onClick={() => handleEdit(item)}
                        className="text-gray-400 hover:text-primary-600 p-1 ml-0.5"
                        aria-label={`Edit ${item.name}`}
                      >
                        <Pencil size={14} />
                      </button>
                    </td>
                  </>
                )}
              </tr>
              {expandedTagsId === item.id && (
                <tr className="bg-gray-50 border-b border-gray-100">
                  <td colSpan={5} className="px-6 py-3">
                    <DefaultTagsEditor eventTypeId={item.id} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Default Tags Editor (embedded in EventTypesTab) ─────────────────────────

const EMPTY_DEFAULT_TAG = { code: '', description: '', type: 'TEXT' as TagType };

function DefaultTagsEditor({ eventTypeId }: { eventTypeId: string }) {
  const { data: tags = [], isLoading } = useEventTypeDefaultTags(eventTypeId);
  const createTag = useCreateEventTypeDefaultTag(eventTypeId);
  const updateTag = useUpdateEventTypeDefaultTag(eventTypeId);
  const deleteTag = useDeleteEventTypeDefaultTag(eventTypeId);

  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY_DEFAULT_TAG);
  const [editingTagId, setEditingTagId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState(EMPTY_DEFAULT_TAG);

  function handleAdd() {
    if (!form.code.trim() || !form.description.trim()) return;
    createTag.mutate(
      {
        code: form.code
          .trim()
          .toUpperCase()
          .replace(/[^A-Z0-9_]/g, '_'),
        description: form.description.trim(),
        type: form.type,
      },
      {
        onSuccess: () => {
          setForm(EMPTY_DEFAULT_TAG);
          setShowAdd(false);
        },
      },
    );
  }

  function startEdit(tag: EventTypeDefaultTag) {
    setEditingTagId(tag.id);
    setEditForm({ code: tag.code, description: tag.description, type: tag.type });
  }

  function saveEdit() {
    if (!editingTagId || !editForm.code.trim() || !editForm.description.trim()) return;
    updateTag.mutate(
      {
        tagId: editingTagId,
        updates: {
          code: editForm.code
            .trim()
            .toUpperCase()
            .replace(/[^A-Z0-9_]/g, '_'),
          description: editForm.description.trim(),
          type: editForm.type,
        },
      },
      { onSuccess: () => setEditingTagId(null) },
    );
  }

  if (isLoading) return <p className="text-xs text-gray-400 py-1">Loading...</p>;

  return (
    <div className="mt-1" data-testid={`default-tags-${eventTypeId}`}>
      <div className="text-xs font-medium text-gray-500 mb-1">Default Tags</div>
      <table className="w-full text-xs mb-2">
        <thead>
          <tr className="text-gray-400 border-b border-gray-100">
            <th className="text-left py-1 pr-2 w-28">Code</th>
            <th className="text-left py-1 pr-2">Description</th>
            <th className="text-left py-1 pr-2 w-20">Type</th>
            <th className="w-12" />
          </tr>
        </thead>
        <tbody>
          {tags.map((tag) => (
            <tr
              key={tag.id}
              className="border-b border-gray-50"
              data-testid={`default-tag-row-${tag.id}`}
            >
              {editingTagId === tag.id ? (
                <>
                  <td className="py-1 pr-2">
                    <input
                      value={editForm.code}
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''),
                        }))
                      }
                      className="w-full border border-gray-300 rounded px-1 py-0.5 text-xs font-mono"
                    />
                  </td>
                  <td className="py-1 pr-2">
                    <input
                      value={editForm.description}
                      onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                      className="w-full border border-gray-300 rounded px-1 py-0.5 text-xs"
                    />
                  </td>
                  <td className="py-1 pr-2">
                    <select
                      value={editForm.type}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, type: e.target.value as TagType }))
                      }
                      className="w-full border border-gray-300 rounded px-1 py-0.5 text-xs"
                    >
                      <option value="TEXT">Text</option>
                      <option value="DATE">Date</option>
                      <option value="NUMBER">Number</option>
                    </select>
                  </td>
                  <td className="py-1 text-right">
                    <button
                      onClick={saveEdit}
                      className="text-green-600 hover:text-green-800 p-0.5"
                      aria-label="Save"
                    >
                      <Check size={12} />
                    </button>
                    <button
                      onClick={() => setEditingTagId(null)}
                      className="text-gray-400 hover:text-gray-600 p-0.5 ml-0.5"
                      aria-label="Cancel"
                    >
                      <X size={12} />
                    </button>
                  </td>
                </>
              ) : (
                <>
                  <td className="py-1 pr-2 font-mono text-primary-700">{`{${tag.code}}`}</td>
                  <td className="py-1 pr-2 text-gray-600">{tag.description}</td>
                  <td className="py-1 pr-2 text-gray-500 capitalize">{tag.type.toLowerCase()}</td>
                  <td className="py-1 text-right">
                    <button
                      onClick={() => startEdit(tag)}
                      className="text-gray-400 hover:text-primary-600 p-0.5"
                      aria-label="Edit tag"
                    >
                      <Pencil size={11} />
                    </button>
                    <button
                      onClick={() => deleteTag.mutate(tag.id)}
                      className="text-gray-400 hover:text-red-600 p-0.5 ml-0.5"
                      aria-label="Delete tag"
                    >
                      <Trash2 size={11} />
                    </button>
                  </td>
                </>
              )}
            </tr>
          ))}
          {showAdd && (
            <tr className="bg-blue-50" data-testid="add-default-tag-row">
              <td className="py-1 pr-2">
                <input
                  value={form.code}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''),
                    }))
                  }
                  placeholder="CODE"
                  className="w-full border border-gray-300 rounded px-1 py-0.5 text-xs font-mono"
                  data-testid="new-default-tag-code"
                />
              </td>
              <td className="py-1 pr-2">
                <input
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Description"
                  className="w-full border border-gray-300 rounded px-1 py-0.5 text-xs"
                  data-testid="new-default-tag-desc"
                />
              </td>
              <td className="py-1 pr-2">
                <select
                  value={form.type}
                  onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as TagType }))}
                  className="w-full border border-gray-300 rounded px-1 py-0.5 text-xs"
                  data-testid="new-default-tag-type"
                >
                  <option value="TEXT">Text</option>
                  <option value="DATE">Date</option>
                  <option value="NUMBER">Number</option>
                </select>
              </td>
              <td className="py-1 text-right">
                <button
                  onClick={handleAdd}
                  disabled={!form.code.trim() || !form.description.trim()}
                  className="text-green-600 hover:text-green-800 p-0.5 disabled:opacity-50"
                  aria-label="Save new tag"
                >
                  <Check size={12} />
                </button>
                <button
                  onClick={() => {
                    setShowAdd(false);
                    setForm(EMPTY_DEFAULT_TAG);
                  }}
                  className="text-gray-400 hover:text-gray-600 p-0.5 ml-0.5"
                  aria-label="Cancel"
                >
                  <X size={12} />
                </button>
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {!showAdd && (
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
          data-testid="add-default-tag-button"
        >
          <Plus size={11} /> Add Default Tag
        </button>
      )}
    </div>
  );
}

// ─── Vendor Categories Tab ───────────────────────────────────────────────────

export function VendorCategoriesTab() {
  const { confirm } = useDialog();
  const { data: items = [], isLoading } = useSettingsVendorCategories();
  const createMutation = useCreateVendorCategory();
  const updateMutation = useUpdateVendorCategory();
  const deleteMutation = useDeleteVendorCategory();
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', nameHe: '' });
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleAdd = useCallback(() => {
    setAdding(true);
    setEditingId(null);
    setForm({ name: '', nameHe: '' });
  }, []);

  const handleEdit = useCallback((item: { id: string; name: string; nameHe: string | null }) => {
    setEditingId(item.id);
    setAdding(false);
    setForm({ name: item.name, nameHe: item.nameHe || '' });
  }, []);

  const handleSaveNew = useCallback(async () => {
    await createMutation.mutateAsync({
      name: form.name,
      ...(form.nameHe && { nameHe: form.nameHe }),
    });
    setAdding(false);
    setForm({ name: '', nameHe: '' });
  }, [createMutation, form]);

  const handleSaveEdit = useCallback(async () => {
    if (!editingId) return;
    await updateMutation.mutateAsync({
      id: editingId,
      updates: { name: form.name, nameHe: form.nameHe || null },
    });
    setEditingId(null);
  }, [editingId, updateMutation, form]);

  const handleToggleActive = useCallback(
    async (id: string, currentActive: boolean) => {
      await updateMutation.mutateAsync({ id, updates: { isActive: !currentActive } });
    },
    [updateMutation],
  );

  const handleDelete = useCallback(
    async (item: { id: string; name: string }) => {
      setDeleteError(null);
      if (
        !(await confirm({
          title: 'Delete Category',
          message: `Delete the category "${item.name}"? This cannot be undone.`,
          confirmLabel: 'Delete',
        }))
      )
        return;
      try {
        await deleteMutation.mutateAsync(item.id);
      } catch (err) {
        const message =
          (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
            ?.message || 'Failed to delete category.';
        setDeleteError(message);
      }
    },
    [deleteMutation, confirm],
  );

  const handleCancel = useCallback(() => {
    setAdding(false);
    setEditingId(null);
    setForm({ name: '', nameHe: '' });
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 size={24} className="animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div>
      {deleteError && (
        <div
          className="m-4 px-3 py-2 rounded-md bg-red-50 border border-red-200 text-sm text-red-700"
          role="alert"
        >
          {deleteError}
        </div>
      )}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Vendor Categories</h2>
        <button
          onClick={handleAdd}
          disabled={adding}
          className="flex items-center gap-1 px-3 py-1.5 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
        >
          <Plus size={14} />
          Add
        </button>
      </div>

      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Name
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Name (Hebrew)
            </th>
            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
              Active
            </th>
            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {adding && (
            <tr className="border-b border-gray-100 bg-blue-50">
              <td className="px-4 py-2">
                <input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Name"
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  data-testid="new-name-input"
                />
              </td>
              <td className="px-4 py-2">
                <input
                  value={form.nameHe}
                  onChange={(e) => setForm((f) => ({ ...f, nameHe: e.target.value }))}
                  placeholder="Name (Hebrew)"
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                />
              </td>
              <td />
              <td className="px-4 py-2 text-right">
                <button
                  onClick={handleSaveNew}
                  disabled={!form.name.trim() || createMutation.isPending}
                  className="text-green-600 hover:text-green-800 p-1 disabled:opacity-50"
                  aria-label="Save new item"
                >
                  <Check size={16} />
                </button>
                <button
                  onClick={handleCancel}
                  className="text-gray-400 hover:text-gray-600 p-1 ml-1"
                  aria-label="Cancel"
                >
                  <X size={16} />
                </button>
              </td>
            </tr>
          )}
          {items.map((item) => (
            <tr key={item.id} className="border-b border-gray-100">
              {editingId === item.id ? (
                <>
                  <td className="px-4 py-2">
                    <input
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                      data-testid="edit-name-input"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <input
                      value={form.nameHe}
                      onChange={(e) => setForm((f) => ({ ...f, nameHe: e.target.value }))}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                    />
                  </td>
                  <td />
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={handleSaveEdit}
                      disabled={!form.name.trim() || updateMutation.isPending}
                      className="text-green-600 hover:text-green-800 p-1 disabled:opacity-50"
                      aria-label="Save edit"
                    >
                      <Check size={16} />
                    </button>
                    <button
                      onClick={handleCancel}
                      className="text-gray-400 hover:text-gray-600 p-1 ml-1"
                      aria-label="Cancel"
                    >
                      <X size={16} />
                    </button>
                  </td>
                </>
              ) : (
                <>
                  <td className="px-4 py-2 text-gray-900">{item.name}</td>
                  <td className="px-4 py-2 text-gray-600">{item.nameHe || '-'}</td>
                  <td className="px-4 py-2 text-center">
                    <button
                      onClick={() => handleToggleActive(item.id, item.isActive)}
                      className={`inline-block w-8 h-4 rounded-full relative transition-colors ${
                        item.isActive ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                      aria-label={item.isActive ? 'Deactivate' : 'Activate'}
                    >
                      <span
                        className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-transform ${
                          item.isActive ? 'left-4' : 'left-0.5'
                        }`}
                      />
                    </button>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => handleEdit(item)}
                      className="text-gray-400 hover:text-primary-600 p-1"
                      aria-label={`Edit ${item.name}`}
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => handleDelete(item)}
                      disabled={deleteMutation.isPending}
                      className="text-gray-400 hover:text-red-600 p-1 ml-1 disabled:opacity-50"
                      aria-label={`Delete ${item.name}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Expense Statuses Tab ────────────────────────────────────────────────────

export function ExpenseStatusesTab() {
  const { data: items = [], isLoading } = useSettingsExpenseStatuses();
  const createMutation = useCreateExpenseStatus();
  const updateMutation = useUpdateExpenseStatus();
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', nameHe: '', color: '#000000', sortOrder: '0' });

  const handleAdd = useCallback(() => {
    setAdding(true);
    setEditingId(null);
    setForm({ name: '', nameHe: '', color: '#000000', sortOrder: '0' });
  }, []);

  const handleEdit = useCallback(
    (item: {
      id: string;
      name: string;
      nameHe: string | null;
      color: string | null;
      sortOrder: number;
    }) => {
      setEditingId(item.id);
      setAdding(false);
      setForm({
        name: item.name,
        nameHe: item.nameHe || '',
        color: item.color || '#000000',
        sortOrder: String(item.sortOrder),
      });
    },
    [],
  );

  const handleSaveNew = useCallback(async () => {
    await createMutation.mutateAsync({
      name: form.name,
      ...(form.nameHe && { nameHe: form.nameHe }),
      ...(form.color && { color: form.color }),
      sortOrder: parseInt(form.sortOrder, 10) || 0,
    });
    setAdding(false);
    setForm({ name: '', nameHe: '', color: '#000000', sortOrder: '0' });
  }, [createMutation, form]);

  const handleSaveEdit = useCallback(async () => {
    if (!editingId) return;
    await updateMutation.mutateAsync({
      id: editingId,
      updates: {
        name: form.name,
        nameHe: form.nameHe || null,
        color: form.color || null,
        sortOrder: parseInt(form.sortOrder, 10) || 0,
      },
    });
    setEditingId(null);
  }, [editingId, updateMutation, form]);

  const handleToggleActive = useCallback(
    async (id: string, currentActive: boolean) => {
      await updateMutation.mutateAsync({ id, updates: { isActive: !currentActive } });
    },
    [updateMutation],
  );

  // Reconciliation resolves a single "fully paid" status: the API clears the
  // previous holder in the same transaction, so this is one call either way.
  const handleTogglePaidInFull = useCallback(
    async (id: string, current: boolean) => {
      await updateMutation.mutateAsync({ id, updates: { isPaidInFull: !current } });
    },
    [updateMutation],
  );

  const handleCancel = useCallback(() => {
    setAdding(false);
    setEditingId(null);
    setForm({ name: '', nameHe: '', color: '#000000', sortOrder: '0' });
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 size={24} className="animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Expense Statuses</h2>
        <button
          onClick={handleAdd}
          disabled={adding}
          className="flex items-center gap-1 px-3 py-1.5 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
        >
          <Plus size={14} />
          Add
        </button>
      </div>

      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Name
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Name (Hebrew)
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Color
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Sort Order
            </th>
            <th
              className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase"
              title="The status an expense moves to once it is fully paid"
            >
              Paid in Full
            </th>
            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
              Active
            </th>
            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {adding && (
            <tr className="border-b border-gray-100 bg-blue-50">
              <td className="px-4 py-2">
                <input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Name"
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  data-testid="new-name-input"
                />
              </td>
              <td className="px-4 py-2">
                <input
                  value={form.nameHe}
                  onChange={(e) => setForm((f) => ({ ...f, nameHe: e.target.value }))}
                  placeholder="Name (Hebrew)"
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                />
              </td>
              <td className="px-4 py-2">
                <input
                  type="color"
                  value={form.color}
                  onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                  className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
                  data-testid="new-color-input"
                />
              </td>
              <td className="px-4 py-2">
                <input
                  type="number"
                  value={form.sortOrder}
                  onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
                  className="w-16 border border-gray-300 rounded px-2 py-1 text-sm"
                />
              </td>
              <td />
              <td />
              <td className="px-4 py-2 text-right">
                <button
                  onClick={handleSaveNew}
                  disabled={!form.name.trim() || createMutation.isPending}
                  className="text-green-600 hover:text-green-800 p-1 disabled:opacity-50"
                  aria-label="Save new item"
                >
                  <Check size={16} />
                </button>
                <button
                  onClick={handleCancel}
                  className="text-gray-400 hover:text-gray-600 p-1 ml-1"
                  aria-label="Cancel"
                >
                  <X size={16} />
                </button>
              </td>
            </tr>
          )}
          {items.map((item) => (
            <tr key={item.id} className="border-b border-gray-100">
              {editingId === item.id ? (
                <>
                  <td className="px-4 py-2">
                    <input
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                      data-testid="edit-name-input"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <input
                      value={form.nameHe}
                      onChange={(e) => setForm((f) => ({ ...f, nameHe: e.target.value }))}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <input
                      type="color"
                      value={form.color}
                      onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                      className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <input
                      type="number"
                      value={form.sortOrder}
                      onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
                      className="w-16 border border-gray-300 rounded px-2 py-1 text-sm"
                    />
                  </td>
                  <td />
                  <td />
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={handleSaveEdit}
                      disabled={!form.name.trim() || updateMutation.isPending}
                      className="text-green-600 hover:text-green-800 p-1 disabled:opacity-50"
                      aria-label="Save edit"
                    >
                      <Check size={16} />
                    </button>
                    <button
                      onClick={handleCancel}
                      className="text-gray-400 hover:text-gray-600 p-1 ml-1"
                      aria-label="Cancel"
                    >
                      <X size={16} />
                    </button>
                  </td>
                </>
              ) : (
                <>
                  <td className="px-4 py-2 text-gray-900">{item.name}</td>
                  <td className="px-4 py-2 text-gray-600">{item.nameHe || '-'}</td>
                  <td className="px-4 py-2">
                    {item.color ? (
                      <span
                        className="inline-block w-6 h-6 rounded border border-gray-200"
                        style={{ backgroundColor: item.color }}
                        title={item.color}
                      />
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="px-4 py-2 text-gray-600">{item.sortOrder}</td>
                  <td className="px-4 py-2 text-center">
                    <SettingToggle
                      checked={item.isPaidInFull}
                      onChange={() => handleTogglePaidInFull(item.id, item.isPaidInFull)}
                      onClassName="bg-primary-600"
                      label={
                        item.isPaidInFull
                          ? `Clear paid in full from ${item.name}`
                          : `Mark ${item.name} as paid in full`
                      }
                    />
                  </td>
                  <td className="px-4 py-2 text-center">
                    <SettingToggle
                      checked={item.isActive}
                      onChange={() => handleToggleActive(item.id, item.isActive)}
                      label={item.isActive ? 'Deactivate' : 'Activate'}
                    />
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => handleEdit(item)}
                      className="text-gray-400 hover:text-primary-600 p-1"
                      aria-label={`Edit ${item.name}`}
                    >
                      <Pencil size={14} />
                    </button>
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Planning Statuses Tab ───────────────────────────────────────────────────

export function PlanningStatusesTab() {
  const { data: items = [], isLoading } = usePlanningStatuses();
  const createMutation = useCreatePlanningStatus();
  const updateMutation = useUpdatePlanningStatus();
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', nameHe: '', color: '#000000', sortOrder: '0' });

  const handleAdd = useCallback(() => {
    setAdding(true);
    setEditingId(null);
    setForm({ name: '', nameHe: '', color: '#000000', sortOrder: '0' });
  }, []);

  const handleEdit = useCallback(
    (item: {
      id: string;
      name: string;
      nameHe: string | null;
      color: string | null;
      sortOrder: number;
    }) => {
      setEditingId(item.id);
      setAdding(false);
      setForm({
        name: item.name,
        nameHe: item.nameHe || '',
        color: item.color || '#000000',
        sortOrder: String(item.sortOrder),
      });
    },
    [],
  );

  const handleSaveNew = useCallback(async () => {
    await createMutation.mutateAsync({
      name: form.name,
      ...(form.nameHe && { nameHe: form.nameHe }),
      ...(form.color && { color: form.color }),
      sortOrder: parseInt(form.sortOrder, 10) || 0,
    });
    setAdding(false);
    setForm({ name: '', nameHe: '', color: '#000000', sortOrder: '0' });
  }, [createMutation, form]);

  const handleSaveEdit = useCallback(async () => {
    if (!editingId) return;
    await updateMutation.mutateAsync({
      id: editingId,
      updates: {
        name: form.name,
        nameHe: form.nameHe || null,
        color: form.color || null,
        sortOrder: parseInt(form.sortOrder, 10) || 0,
      },
    });
    setEditingId(null);
  }, [editingId, updateMutation, form]);

  const handleToggleActive = useCallback(
    async (id: string, currentActive: boolean) => {
      await updateMutation.mutateAsync({ id, updates: { isActive: !currentActive } });
    },
    [updateMutation],
  );

  const handleCancel = useCallback(() => {
    setAdding(false);
    setEditingId(null);
    setForm({ name: '', nameHe: '', color: '#000000', sortOrder: '0' });
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 size={24} className="animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Planning Statuses</h2>
        <button
          onClick={handleAdd}
          disabled={adding}
          className="flex items-center gap-1 px-3 py-1.5 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
        >
          <Plus size={14} />
          Add
        </button>
      </div>

      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Name
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Name (Hebrew)
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Color
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Sort Order
            </th>
            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
              Active
            </th>
            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {adding && (
            <tr className="border-b border-gray-100 bg-blue-50">
              <td className="px-4 py-2">
                <input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Name"
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  data-testid="new-planning-name-input"
                />
              </td>
              <td className="px-4 py-2">
                <input
                  value={form.nameHe}
                  onChange={(e) => setForm((f) => ({ ...f, nameHe: e.target.value }))}
                  placeholder="Name (Hebrew)"
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                />
              </td>
              <td className="px-4 py-2">
                <input
                  type="color"
                  value={form.color}
                  onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                  className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
                  data-testid="new-planning-color-input"
                />
              </td>
              <td className="px-4 py-2">
                <input
                  type="number"
                  value={form.sortOrder}
                  onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
                  className="w-16 border border-gray-300 rounded px-2 py-1 text-sm"
                />
              </td>
              <td />
              <td className="px-4 py-2 text-right">
                <button
                  onClick={handleSaveNew}
                  disabled={!form.name.trim() || createMutation.isPending}
                  className="text-green-600 hover:text-green-800 p-1 disabled:opacity-50"
                  aria-label="Save new item"
                >
                  <Check size={16} />
                </button>
                <button
                  onClick={handleCancel}
                  className="text-gray-400 hover:text-gray-600 p-1 ml-1"
                  aria-label="Cancel"
                >
                  <X size={16} />
                </button>
              </td>
            </tr>
          )}
          {items.map((item) => (
            <tr key={item.id} className="border-b border-gray-100">
              {editingId === item.id ? (
                <>
                  <td className="px-4 py-2">
                    <input
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                      data-testid="edit-planning-name-input"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <input
                      value={form.nameHe}
                      onChange={(e) => setForm((f) => ({ ...f, nameHe: e.target.value }))}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <input
                      type="color"
                      value={form.color}
                      onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                      className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <input
                      type="number"
                      value={form.sortOrder}
                      onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
                      className="w-16 border border-gray-300 rounded px-2 py-1 text-sm"
                    />
                  </td>
                  <td />
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={handleSaveEdit}
                      disabled={!form.name.trim() || updateMutation.isPending}
                      className="text-green-600 hover:text-green-800 p-1 disabled:opacity-50"
                      aria-label="Save edit"
                    >
                      <Check size={16} />
                    </button>
                    <button
                      onClick={handleCancel}
                      className="text-gray-400 hover:text-gray-600 p-1 ml-1"
                      aria-label="Cancel"
                    >
                      <X size={16} />
                    </button>
                  </td>
                </>
              ) : (
                <>
                  <td className="px-4 py-2 text-gray-900">{item.name}</td>
                  <td className="px-4 py-2 text-gray-600">{item.nameHe || '-'}</td>
                  <td className="px-4 py-2">
                    {item.color ? (
                      <span
                        className="inline-block w-6 h-6 rounded border border-gray-200"
                        style={{ backgroundColor: item.color }}
                        title={item.color}
                      />
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="px-4 py-2 text-gray-600">{item.sortOrder}</td>
                  <td className="px-4 py-2 text-center">
                    <button
                      onClick={() => handleToggleActive(item.id, item.isActive)}
                      className={`inline-block w-8 h-4 rounded-full relative transition-colors ${
                        item.isActive ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                      aria-label={item.isActive ? 'Deactivate' : 'Activate'}
                    >
                      <span
                        className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-transform ${
                          item.isActive ? 'left-4' : 'left-0.5'
                        }`}
                      />
                    </button>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => handleEdit(item)}
                      className="text-gray-400 hover:text-primary-600 p-1"
                      aria-label={`Edit ${item.name}`}
                    >
                      <Pencil size={14} />
                    </button>
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Payment Methods Tab ─────────────────────────────────────────────────────

export function PaymentMethodsTab() {
  const { data: items = [], isLoading } = useSettingsPaymentMethods();
  const createMutation = useCreatePaymentMethod();
  const updateMutation = useUpdatePaymentMethod();
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', nameHe: '', isProducerFunded: false });

  const handleAdd = useCallback(() => {
    setAdding(true);
    setEditingId(null);
    setForm({ name: '', nameHe: '', isProducerFunded: false });
  }, []);

  const handleEdit = useCallback(
    (item: { id: string; name: string; nameHe: string | null; isProducerFunded?: boolean }) => {
      setEditingId(item.id);
      setAdding(false);
      setForm({
        name: item.name,
        nameHe: item.nameHe || '',
        isProducerFunded: Boolean(item.isProducerFunded),
      });
    },
    [],
  );

  const handleSaveNew = useCallback(async () => {
    await createMutation.mutateAsync({
      name: form.name,
      ...(form.nameHe && { nameHe: form.nameHe }),
      isProducerFunded: form.isProducerFunded,
    });
    setAdding(false);
    setForm({ name: '', nameHe: '', isProducerFunded: false });
  }, [createMutation, form]);

  const handleSaveEdit = useCallback(async () => {
    if (!editingId) return;
    await updateMutation.mutateAsync({
      id: editingId,
      updates: {
        name: form.name,
        nameHe: form.nameHe || null,
        isProducerFunded: form.isProducerFunded,
      },
    });
    setEditingId(null);
  }, [editingId, updateMutation, form]);

  const handleToggleActive = useCallback(
    async (id: string, currentActive: boolean) => {
      await updateMutation.mutateAsync({ id, updates: { isActive: !currentActive } });
    },
    [updateMutation],
  );

  const handleToggleProducerFunded = useCallback(
    async (id: string, current: boolean) => {
      await updateMutation.mutateAsync({ id, updates: { isProducerFunded: !current } });
    },
    [updateMutation],
  );

  const handleCancel = useCallback(() => {
    setAdding(false);
    setEditingId(null);
    setForm({ name: '', nameHe: '', isProducerFunded: false });
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 size={24} className="animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Payment Methods</h2>
        <button
          onClick={handleAdd}
          disabled={adding}
          className="flex items-center gap-1 px-3 py-1.5 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
        >
          <Plus size={14} />
          Add
        </button>
      </div>

      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Name
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Name (Hebrew)
            </th>
            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
              Active
            </th>
            <th
              className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase"
              title="Producer-funded methods (e.g. NPEP) generate a client reimbursement balance."
            >
              Producer-Funded
            </th>
            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {adding && (
            <tr className="border-b border-gray-100 bg-blue-50">
              <td className="px-4 py-2">
                <input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Name"
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  data-testid="new-name-input"
                />
              </td>
              <td className="px-4 py-2">
                <input
                  value={form.nameHe}
                  onChange={(e) => setForm((f) => ({ ...f, nameHe: e.target.value }))}
                  placeholder="Name (Hebrew)"
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                />
              </td>
              <td />
              <td className="px-4 py-2 text-center">
                <input
                  type="checkbox"
                  checked={form.isProducerFunded}
                  onChange={(e) => setForm((f) => ({ ...f, isProducerFunded: e.target.checked }))}
                  data-testid="new-producer-funded-input"
                />
              </td>
              <td className="px-4 py-2 text-right">
                <button
                  onClick={handleSaveNew}
                  disabled={!form.name.trim() || createMutation.isPending}
                  className="text-green-600 hover:text-green-800 p-1 disabled:opacity-50"
                  aria-label="Save new item"
                >
                  <Check size={16} />
                </button>
                <button
                  onClick={handleCancel}
                  className="text-gray-400 hover:text-gray-600 p-1 ml-1"
                  aria-label="Cancel"
                >
                  <X size={16} />
                </button>
              </td>
            </tr>
          )}
          {items.map((item) => (
            <tr key={item.id} className="border-b border-gray-100">
              {editingId === item.id ? (
                <>
                  <td className="px-4 py-2">
                    <input
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                      data-testid="edit-name-input"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <input
                      value={form.nameHe}
                      onChange={(e) => setForm((f) => ({ ...f, nameHe: e.target.value }))}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                    />
                  </td>
                  <td />
                  <td className="px-4 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={form.isProducerFunded}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, isProducerFunded: e.target.checked }))
                      }
                    />
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={handleSaveEdit}
                      disabled={!form.name.trim() || updateMutation.isPending}
                      className="text-green-600 hover:text-green-800 p-1 disabled:opacity-50"
                      aria-label="Save edit"
                    >
                      <Check size={16} />
                    </button>
                    <button
                      onClick={handleCancel}
                      className="text-gray-400 hover:text-gray-600 p-1 ml-1"
                      aria-label="Cancel"
                    >
                      <X size={16} />
                    </button>
                  </td>
                </>
              ) : (
                <>
                  <td className="px-4 py-2 text-gray-900">{item.name}</td>
                  <td className="px-4 py-2 text-gray-600">{item.nameHe || '-'}</td>
                  <td className="px-4 py-2 text-center">
                    <button
                      onClick={() => handleToggleActive(item.id, item.isActive)}
                      className={`inline-block w-8 h-4 rounded-full relative transition-colors ${
                        item.isActive ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                      aria-label={item.isActive ? 'Deactivate' : 'Activate'}
                    >
                      <span
                        className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-transform ${
                          item.isActive ? 'left-4' : 'left-0.5'
                        }`}
                      />
                    </button>
                  </td>
                  <td className="px-4 py-2 text-center">
                    <button
                      onClick={() => handleToggleProducerFunded(item.id, item.isProducerFunded)}
                      className={`inline-block w-8 h-4 rounded-full relative transition-colors ${
                        item.isProducerFunded ? 'bg-amber-500' : 'bg-gray-300'
                      }`}
                      aria-label={
                        item.isProducerFunded
                          ? 'Disable producer funding'
                          : 'Enable producer funding'
                      }
                      title="If on, this method represents money fronted by the producer that the client must reimburse."
                    >
                      <span
                        className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-transform ${
                          item.isProducerFunded ? 'left-4' : 'left-0.5'
                        }`}
                      />
                    </button>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => handleEdit(item)}
                      className="text-gray-400 hover:text-primary-600 p-1"
                      aria-label={`Edit ${item.name}`}
                    >
                      <Pencil size={14} />
                    </button>
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Catalog Items Tab ───────────────────────────────────────────────────────

export function CatalogItemsTab() {
  const { data: items = [], isLoading } = useSettingsCatalogItems();
  const { data: categories = [] } = useVendorCatsForCatalog();
  // Only active categories are selectable/filterable (sorted alphabetically);
  // items pointing at an inactive or deleted category are surfaced as "Other".
  const activeCategories = useMemo(
    () => categories.filter((c) => c.isActive).sort((a, b) => a.name.localeCompare(b.name)),
    [categories],
  );
  const createMutation = useCreateSettingsCatalogItem();
  const updateMutation = useUpdateSettingsCatalogItem();
  const deleteMutation = useDeleteSettingsCatalogItem();
  const confirmDuplicate = useDuplicateNameGuard();
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', nameHe: '', vendorCategoryId: '' });
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const { data: deleteUsage } = useCatalogItemUsage(deleteTarget?.id ?? '', !!deleteTarget);

  const handleAdd = useCallback(() => {
    setAdding(true);
    setEditingId(null);
    // When filtered to a single category, default the new item to it.
    const defaultCategoryId =
      categoryFilter.length === 1 ? categoryFilter[0] : activeCategories[0]?.id || '';
    setForm({ name: '', nameHe: '', vendorCategoryId: defaultCategoryId });
  }, [activeCategories, categoryFilter]);

  const handleEdit = useCallback(
    (item: { id: string; name: string; nameHe: string | null; vendorCategoryId: string }) => {
      setEditingId(item.id);
      setAdding(false);
      setForm({
        name: item.name,
        nameHe: item.nameHe || '',
        vendorCategoryId: item.vendorCategoryId,
      });
    },
    [],
  );

  const handleSaveNew = useCallback(async () => {
    const duplicateCategoryNames = [
      ...new Set(
        items
          .filter((i) => isSameName(i.name, form.name))
          .map((i) => categories.find((c) => c.id === i.vendorCategoryId)?.name ?? 'Unknown'),
      ),
    ];
    if (
      duplicateCategoryNames.length > 0 &&
      !(await confirmDuplicate({
        entityLabel: 'catalog item',
        name: form.name.trim(),
        detail: `(category: ${duplicateCategoryNames.join(', ')})`,
      }))
    ) {
      return;
    }
    await createMutation.mutateAsync({
      name: form.name,
      vendorCategoryId: form.vendorCategoryId,
      ...(form.nameHe && { nameHe: form.nameHe }),
    });
    setAdding(false);
    setForm({ name: '', nameHe: '', vendorCategoryId: '' });
  }, [createMutation, form, items, categories, confirmDuplicate]);

  const handleSaveEdit = useCallback(async () => {
    if (!editingId) return;
    await updateMutation.mutateAsync({
      id: editingId,
      updates: {
        name: form.name,
        nameHe: form.nameHe || null,
        ...(form.vendorCategoryId && { vendorCategoryId: form.vendorCategoryId }),
      },
    });
    setEditingId(null);
  }, [editingId, updateMutation, form]);

  const handleToggleActive = useCallback(
    async (id: string, currentActive: boolean) => {
      await updateMutation.mutateAsync({ id, updates: { isActive: !currentActive } });
    },
    [updateMutation],
  );

  const handleCancel = useCallback(() => {
    setAdding(false);
    setEditingId(null);
    setForm({ name: '', nameHe: '', vendorCategoryId: '' });
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 size={24} className="animate-spin text-primary-600" />
      </div>
    );
  }

  const displayedItems =
    categoryFilter.length > 0
      ? items.filter((item) => categoryFilter.includes(item.vendorCategoryId))
      : items;

  return (
    <div>
      <div className="p-4 border-b border-gray-200 flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-lg font-semibold text-gray-900">Catalog Items</h2>
        <div className="flex items-center gap-2">
          <FilterSelect
            label="All Categories"
            items={activeCategories}
            value={categoryFilter}
            onChange={(v) => setCategoryFilter(v as string[])}
            getValue={(c) => c.id}
            getLabel={(c) => c.name}
            multiple
          />
          <button
            onClick={handleAdd}
            disabled={adding}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            <Plus size={14} />
            Add
          </button>
        </div>
      </div>

      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Category
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Name
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Name (Hebrew)
            </th>
            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
              Active
            </th>
            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {adding && (
            <tr className="border-b border-gray-100 bg-blue-50">
              <td className="px-4 py-2">
                <SearchableListSelect
                  items={activeCategories}
                  value={form.vendorCategoryId}
                  onChange={(v) => setForm((f) => ({ ...f, vendorCategoryId: v as string }))}
                  getValue={(c) => c.id}
                  getLabel={(c) => c.name}
                  placeholder="Select category"
                  ariaLabel="Category"
                  inputClassName="text-sm"
                />
              </td>
              <td className="px-4 py-2">
                <input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Name"
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  data-testid="new-name-input"
                />
              </td>
              <td className="px-4 py-2">
                <input
                  value={form.nameHe}
                  onChange={(e) => setForm((f) => ({ ...f, nameHe: e.target.value }))}
                  placeholder="Name (Hebrew)"
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                />
              </td>
              <td />
              <td className="px-4 py-2 text-right">
                <button
                  onClick={handleSaveNew}
                  disabled={!form.name.trim() || !form.vendorCategoryId || createMutation.isPending}
                  className="text-green-600 hover:text-green-800 p-1 disabled:opacity-50"
                  aria-label="Save new item"
                >
                  <Check size={16} />
                </button>
                <button
                  onClick={handleCancel}
                  className="text-gray-400 hover:text-gray-600 p-1 ml-1"
                  aria-label="Cancel"
                >
                  <X size={16} />
                </button>
              </td>
            </tr>
          )}
          {displayedItems.map((item) => {
            // Items whose category is inactive or deleted show as "Other".
            const categoryName =
              activeCategories.find((c) => c.id === item.vendorCategoryId)?.name || 'Other';

            return (
              <tr key={item.id} className="border-b border-gray-100">
                {editingId === item.id ? (
                  <>
                    <td className="px-4 py-2">
                      <SearchableListSelect
                        items={activeCategories}
                        value={form.vendorCategoryId}
                        onChange={(v) => setForm((f) => ({ ...f, vendorCategoryId: v as string }))}
                        getValue={(c) => c.id}
                        getLabel={(c) => c.name}
                        placeholder="Select category"
                        ariaLabel="Edit category"
                        inputClassName="text-sm"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        value={form.name}
                        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                        data-testid="edit-name-input"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        value={form.nameHe}
                        onChange={(e) => setForm((f) => ({ ...f, nameHe: e.target.value }))}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                      />
                    </td>
                    <td />
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={handleSaveEdit}
                        disabled={!form.name.trim() || updateMutation.isPending}
                        className="text-green-600 hover:text-green-800 p-1 disabled:opacity-50"
                        aria-label="Save edit"
                      >
                        <Check size={16} />
                      </button>
                      <button
                        onClick={handleCancel}
                        className="text-gray-400 hover:text-gray-600 p-1 ml-1"
                        aria-label="Cancel"
                      >
                        <X size={16} />
                      </button>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-4 py-2 text-gray-600">{categoryName}</td>
                    <td className="px-4 py-2 text-gray-900">{item.name}</td>
                    <td className="px-4 py-2 text-gray-600">{item.nameHe || '-'}</td>
                    <td className="px-4 py-2 text-center">
                      <button
                        onClick={() => handleToggleActive(item.id, item.isActive)}
                        className={`inline-block w-8 h-4 rounded-full relative transition-colors ${
                          item.isActive ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                        aria-label={item.isActive ? 'Deactivate' : 'Activate'}
                      >
                        <span
                          className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-transform ${
                            item.isActive ? 'left-4' : 'left-0.5'
                          }`}
                        />
                      </button>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={() => handleEdit(item)}
                        className="text-gray-400 hover:text-primary-600 p-1"
                        aria-label={`Edit ${item.name}`}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => setDeleteTarget({ id: item.id, name: item.name })}
                        className="text-gray-400 hover:text-red-600 p-1"
                        aria-label={`Delete ${item.name}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>

      <ReassignDeleteModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        entityTypeLabel="catalog item"
        entityName={deleteTarget?.name ?? ''}
        usage={
          deleteUsage && [
            { label: 'Expense lines', count: deleteUsage.expenseItems },
            { label: 'Itemized / planning lines', count: deleteUsage.planningItems },
          ]
        }
        options={items.map((i) => ({
          id: i.id,
          label: i.name,
          subtitle: categories.find((c) => c.id === i.vendorCategoryId)?.name,
        }))}
        excludeId={deleteTarget?.id ?? ''}
        onConfirm={async (reassignToId) => {
          if (!deleteTarget) return;
          await deleteMutation.mutateAsync({
            id: deleteTarget.id,
            reassignToCatalogItemId: reassignToId,
          });
        }}
        deleting={deleteMutation.isPending}
      />
    </div>
  );
}

// ─── App Settings Tab ───────────────────────────────────────────────────────

const VAR_TYPES: AppSettingVarType[] = [
  'TEXT',
  'NUMBER',
  'DATE',
  'BOOLEAN',
  'USER',
  'VENDOR',
  'VENUE',
  'LIST',
];

const LIST_ITEM_TYPES: AppSettingListItemType[] = ['TEXT', 'NUMBER', 'USER', 'VENDOR', 'VENUE'];

interface AppSettingForm {
  varCode: string;
  name: string;
  description: string;
  varType: AppSettingVarType;
  value: string;
  listCode: string;
}

const emptySettingForm: AppSettingForm = {
  varCode: '',
  name: '',
  description: '',
  varType: 'TEXT',
  value: '',
  listCode: '',
};

interface AppSettingListForm {
  listCode: string;
  name: string;
  description: string;
  itemType: AppSettingListItemType;
  items: string;
}

const emptyListForm: AppSettingListForm = {
  listCode: '',
  name: '',
  description: '',
  itemType: 'TEXT',
  items: '[]',
};

// ─── Floor Plan Objects Tab ────────────────────────────────────────────────

export function FloorPlanObjectsTab() {
  const { confirm } = useDialog();
  const { data: objectTypes = [], isLoading } = useSettingsFloorPlanObjectTypes();
  const createObjectType = useCreateFloorPlanObjectType();
  const updateObjectType = useUpdateFloorPlanObjectType();
  const deleteObjectType = useDeleteFloorPlanObjectType();

  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    widthFt: '5',
    heightFt: '5',
    defaultColor: '#e0e0e0',
    defaultSeats: '',
    icon: '',
    shape: 'RECTANGLE',
    strokeColor: '',
    canHaveChairs: false,
    chairSizeFt: '',
    isTable: false,
    isBackground: false,
    aspectRatioLocked: false,
  });

  const resetForm = useCallback(() => {
    setFormData({
      name: '',
      category: '',
      widthFt: '5',
      heightFt: '5',
      defaultColor: '#e0e0e0',
      defaultSeats: '',
      icon: '',
      shape: 'RECTANGLE',
      strokeColor: '',
      canHaveChairs: false,
      chairSizeFt: '',
      isTable: false,
      isBackground: false,
      aspectRatioLocked: false,
    });
  }, []);

  const handleAdd = useCallback(() => {
    createObjectType.mutate(
      {
        name: formData.name,
        category: formData.category,
        widthFt: parseFloat(formData.widthFt),
        heightFt: parseFloat(formData.heightFt),
        defaultColor: formData.defaultColor,
        defaultSeats: formData.defaultSeats ? parseInt(formData.defaultSeats) : undefined,
        icon: formData.icon || undefined,
        shape: formData.shape as import('@peles/shared').FloorPlanObjectShape,
        strokeColor: formData.strokeColor || undefined,
        canHaveChairs: formData.isTable || formData.canHaveChairs,
        chairSizeFt:
          formData.isTable || formData.canHaveChairs
            ? formData.chairSizeFt
              ? parseFloat(formData.chairSizeFt)
              : undefined
            : undefined,
        isTable: formData.isTable,
        isBackground: formData.isBackground,
        aspectRatioLocked: formData.aspectRatioLocked,
      },
      {
        onSuccess: () => {
          setShowAdd(false);
          resetForm();
        },
      },
    );
  }, [formData, createObjectType, resetForm]);

  const handleSaveEdit = useCallback(() => {
    if (!editingId) return;
    updateObjectType.mutate(
      {
        id: editingId,
        updates: {
          name: formData.name,
          category: formData.category,
          widthFt: parseFloat(formData.widthFt),
          heightFt: parseFloat(formData.heightFt),
          defaultColor: formData.defaultColor,
          defaultSeats: formData.defaultSeats ? parseInt(formData.defaultSeats) : null,
          icon: formData.icon || null,
          shape: formData.shape as import('@peles/shared').FloorPlanObjectShape,
          strokeColor: formData.strokeColor || null,
          canHaveChairs: formData.isTable || formData.canHaveChairs,
          chairSizeFt:
            formData.isTable || formData.canHaveChairs
              ? formData.chairSizeFt
                ? parseFloat(formData.chairSizeFt)
                : null
              : null,
          isTable: formData.isTable,
          isBackground: formData.isBackground,
          aspectRatioLocked: formData.aspectRatioLocked,
        },
      },
      {
        onSuccess: () => {
          setEditingId(null);
          resetForm();
        },
      },
    );
  }, [editingId, formData, updateObjectType, resetForm]);

  const handleDelete = useCallback(
    async (id: string) => {
      if (
        await confirm({
          title: 'Delete Object Type',
          message: 'Delete this object type?',
          confirmLabel: 'Delete',
        })
      ) {
        deleteObjectType.mutate(id);
      }
    },
    [deleteObjectType, confirm],
  );

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader2 className="animate-spin text-gray-400" size={20} />
      </div>
    );
  }

  const renderForm = (onSave: () => void, onCancel: () => void) => (
    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 space-y-2">
      <div className="flex items-end gap-2 flex-wrap">
        <div className="flex-1 min-w-32">
          <label className="block text-xs text-gray-500">Name</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData((f) => ({ ...f, name: e.target.value }))}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            placeholder='Round 60"'
          />
        </div>
        <div className="w-24">
          <label className="block text-xs text-gray-500">Category</label>
          <input
            type="text"
            value={formData.category}
            onChange={(e) => setFormData((f) => ({ ...f, category: e.target.value }))}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            placeholder="Tables"
          />
        </div>
        <div className="w-14">
          <label className="block text-xs text-gray-500">Icon</label>
          <input
            type="text"
            value={formData.icon}
            onChange={(e) => setFormData((f) => ({ ...f, icon: e.target.value }))}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm text-center"
            placeholder="🪑"
            maxLength={4}
          />
        </div>
        <div className="w-16">
          <label className="block text-xs text-gray-500">W (ft)</label>
          <input
            type="number"
            value={formData.widthFt}
            onChange={(e) => setFormData((f) => ({ ...f, widthFt: e.target.value }))}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
          />
        </div>
        <div className="w-16">
          <label className="block text-xs text-gray-500">H (ft)</label>
          <input
            type="number"
            value={formData.heightFt}
            onChange={(e) => setFormData((f) => ({ ...f, heightFt: e.target.value }))}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
          />
        </div>
        {formData.isTable && (
          <div className="w-16">
            <label className="block text-xs text-gray-500">Seats</label>
            <input
              type="number"
              value={formData.defaultSeats}
              onChange={(e) => setFormData((f) => ({ ...f, defaultSeats: e.target.value }))}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            />
          </div>
        )}
        <div className="w-12">
          <label className="block text-xs text-gray-500">Fill</label>
          <input
            type="color"
            value={formData.defaultColor}
            onChange={(e) => setFormData((f) => ({ ...f, defaultColor: e.target.value }))}
            className="w-full h-7 border border-gray-300 rounded cursor-pointer"
          />
        </div>
        <div className="w-12">
          <label className="block text-xs text-gray-500">Stroke</label>
          <input
            type="color"
            value={formData.strokeColor || '#6b7280'}
            onChange={(e) => setFormData((f) => ({ ...f, strokeColor: e.target.value }))}
            className="w-full h-7 border border-gray-300 rounded cursor-pointer"
          />
        </div>
      </div>
      <div className="flex items-end gap-2 flex-wrap">
        <div className="w-36">
          <label className="block text-xs text-gray-500">Shape</label>
          <select
            value={formData.shape}
            onChange={(e) => setFormData((f) => ({ ...f, shape: e.target.value }))}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
          >
            <option value="RECTANGLE">Rectangle</option>
            <option value="CIRCLE">Circle</option>
            <option value="OVAL">Oval</option>
            <option value="ROUNDED_RECT">Rounded Rect</option>
            <option value="DIAMOND">Diamond</option>
            <option value="HEXAGON">Hexagon</option>
            <option value="SERPENTINE">Serpentine</option>
          </select>
        </div>
        <div className="flex items-center gap-1 mt-4">
          <input
            type="checkbox"
            id="fp-isTable"
            checked={formData.isTable}
            onChange={(e) =>
              setFormData((f) => ({
                ...f,
                isTable: e.target.checked,
                canHaveChairs: e.target.checked || f.canHaveChairs,
              }))
            }
            className="rounded"
          />
          <label htmlFor="fp-isTable" className="text-xs text-gray-600 font-medium">
            Table
          </label>
        </div>
        {!formData.isTable && (
          <div className="flex items-center gap-1 mt-4">
            <input
              type="checkbox"
              id="fp-canHaveChairs"
              checked={formData.canHaveChairs}
              onChange={(e) => setFormData((f) => ({ ...f, canHaveChairs: e.target.checked }))}
              className="rounded"
            />
            <label htmlFor="fp-canHaveChairs" className="text-xs text-gray-600">
              Chairs
            </label>
          </div>
        )}
        {(formData.isTable || formData.canHaveChairs) && (
          <div className="w-20">
            <label className="block text-xs text-gray-500">Chair size (ft)</label>
            <input
              type="number"
              step="0.1"
              value={formData.chairSizeFt}
              onChange={(e) => setFormData((f) => ({ ...f, chairSizeFt: e.target.value }))}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              placeholder="1.5"
            />
          </div>
        )}
        <div className="flex items-center gap-1 mt-4">
          <input
            type="checkbox"
            id="fp-isBackground"
            checked={formData.isBackground}
            onChange={(e) => setFormData((f) => ({ ...f, isBackground: e.target.checked }))}
            className="rounded"
          />
          <label htmlFor="fp-isBackground" className="text-xs text-gray-600">
            Background
          </label>
        </div>
        <div className="flex items-center gap-1 mt-4">
          <input
            type="checkbox"
            id="fp-aspectRatioLocked"
            checked={formData.aspectRatioLocked}
            onChange={(e) => setFormData((f) => ({ ...f, aspectRatioLocked: e.target.checked }))}
            className="rounded"
          />
          <label htmlFor="fp-aspectRatioLocked" className="text-xs text-gray-600">
            Lock ratio
          </label>
        </div>
        <div className="flex gap-1 mt-4 ml-auto">
          <button
            onClick={onSave}
            disabled={!formData.name.trim() || !formData.category.trim()}
            className="p-1.5 text-green-600 hover:bg-green-50 rounded disabled:opacity-50"
          >
            <Check size={16} />
          </button>
          <button onClick={onCancel} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded">
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900">Floor Plan Objects</h2>
        <button
          onClick={() => {
            resetForm();
            setShowAdd(true);
          }}
          className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
        >
          <Plus size={16} />
          Add Object
        </button>
      </div>

      {showAdd && renderForm(handleAdd, () => setShowAdd(false))}

      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="text-left px-4 py-2 font-medium text-gray-600">Name</th>
            <th className="text-left px-4 py-2 font-medium text-gray-600">Category</th>
            <th className="text-left px-4 py-2 font-medium text-gray-600">Size</th>
            <th className="text-left px-4 py-2 font-medium text-gray-600">Shape</th>
            <th className="text-left px-4 py-2 font-medium text-gray-600">Seats</th>
            <th className="text-left px-4 py-2 font-medium text-gray-600">Color</th>
            <th className="text-left px-4 py-2 font-medium text-gray-600">Flags</th>
            <th className="text-left px-4 py-2 font-medium text-gray-600">Active</th>
            <th className="px-4 py-2"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {objectTypes.map((ot) =>
            editingId === ot.id ? (
              <tr key={ot.id}>
                <td colSpan={9} className="p-0">
                  {renderForm(handleSaveEdit, () => setEditingId(null))}
                </td>
              </tr>
            ) : (
              <tr key={ot.id} className="hover:bg-gray-50">
                <td className="px-4 py-2 text-gray-900">{ot.name}</td>
                <td className="px-4 py-2 text-gray-600">{ot.category}</td>
                <td className="px-4 py-2 text-gray-600">
                  {ot.widthFt}&apos; x {ot.heightFt}&apos;
                </td>
                <td className="px-4 py-2 text-gray-500 text-xs capitalize">
                  {(ot.shape ?? 'RECTANGLE').toLowerCase().replace('_', ' ')}
                </td>
                <td className="px-4 py-2 text-gray-600">{ot.defaultSeats ?? '-'}</td>
                <td className="px-4 py-2">
                  <span
                    className="inline-block w-5 h-5 rounded border border-gray-300"
                    style={{ backgroundColor: ot.defaultColor }}
                  />
                </td>
                <td className="px-4 py-2 text-xs text-gray-500 space-x-1">
                  {ot.canHaveChairs && (
                    <span className="bg-blue-50 text-blue-600 px-1 rounded">chairs</span>
                  )}
                  {ot.isBackground && (
                    <span className="bg-gray-100 text-gray-500 px-1 rounded">bg</span>
                  )}
                  {ot.aspectRatioLocked && (
                    <span className="bg-amber-50 text-amber-600 px-1 rounded">locked</span>
                  )}
                </td>
                <td className="px-4 py-2">
                  <button
                    onClick={() =>
                      updateObjectType.mutate({
                        id: ot.id,
                        updates: { isActive: !ot.isActive },
                      })
                    }
                    className={`text-xs px-2 py-0.5 rounded ${
                      ot.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {ot.isActive ? 'Active' : 'Inactive'}
                  </button>
                </td>
                <td className="px-4 py-2 text-right">
                  <button
                    onClick={() => {
                      setEditingId(ot.id);
                      setFormData({
                        name: ot.name,
                        category: ot.category,
                        widthFt: String(ot.widthFt),
                        heightFt: String(ot.heightFt),
                        defaultColor: ot.defaultColor,
                        defaultSeats: ot.defaultSeats != null ? String(ot.defaultSeats) : '',
                        icon: ot.icon ?? '',
                        shape: ot.shape ?? 'RECTANGLE',
                        strokeColor: ot.strokeColor ?? '',
                        canHaveChairs: ot.canHaveChairs ?? false,
                        chairSizeFt: ot.chairSizeFt != null ? String(ot.chairSizeFt) : '',
                        isTable: ot.isTable ?? false,
                        isBackground: ot.isBackground ?? false,
                        aspectRatioLocked: ot.aspectRatioLocked ?? false,
                      });
                    }}
                    className="p-1 text-gray-400 hover:text-primary-600"
                    title="Edit"
                  >
                    <Pencil size={14} />
                  </button>
                  {!ot.isDefault && (
                    <button
                      onClick={() => handleDelete(ot.id)}
                      className="p-1 text-gray-400 hover:text-red-600"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ),
          )}
        </tbody>
      </table>
    </div>
  );
}

export function AppSettingsTab() {
  const { data: settings = [], isLoading: settingsLoading } = useAppSettings();
  const { data: lists = [], isLoading: listsLoading } = useAppSettingLists();
  const createSetting = useCreateAppSetting();
  const updateSetting = useUpdateAppSetting();
  const deleteSetting = useDeleteAppSetting();
  const createList = useCreateAppSettingList();
  const updateList = useUpdateAppSettingList();
  const deleteList = useDeleteAppSettingList();

  const [view, setView] = useState<'settings' | 'lists'>('settings');
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [settingForm, setSettingForm] = useState<AppSettingForm>(emptySettingForm);
  const [listForm, setListForm] = useState<AppSettingListForm>(emptyListForm);

  // ─── Setting Handlers ───────────────────────────────────────────

  const handleAddSetting = useCallback(() => {
    setAdding(true);
    setEditingId(null);
    setSettingForm(emptySettingForm);
  }, []);

  const handleEditSetting = useCallback(
    (item: {
      id: string;
      varCode: string;
      name: string;
      description: string | null;
      varType: string;
      value: string | null;
      listCode: string | null;
    }) => {
      setEditingId(item.id);
      setAdding(false);
      setSettingForm({
        varCode: item.varCode,
        name: item.name,
        description: item.description || '',
        varType: item.varType as AppSettingVarType,
        value: item.value || '',
        listCode: item.listCode || '',
      });
    },
    [],
  );

  const handleSaveNewSetting = useCallback(async () => {
    await createSetting.mutateAsync({
      varCode: settingForm.varCode,
      name: settingForm.name,
      varType: settingForm.varType,
      ...(settingForm.description && { description: settingForm.description }),
      ...(settingForm.value && { value: settingForm.value }),
      ...(settingForm.listCode && { listCode: settingForm.listCode }),
    });
    setAdding(false);
    setSettingForm(emptySettingForm);
  }, [createSetting, settingForm]);

  const handleSaveEditSetting = useCallback(async () => {
    if (!editingId) return;
    await updateSetting.mutateAsync({
      id: editingId,
      updates: {
        name: settingForm.name,
        description: settingForm.description || null,
        varType: settingForm.varType,
        value: settingForm.value || null,
        listCode: settingForm.listCode || null,
      },
    });
    setEditingId(null);
  }, [editingId, updateSetting, settingForm]);

  const handleDeleteSetting = useCallback(
    async (id: string) => {
      await deleteSetting.mutateAsync(id);
    },
    [deleteSetting],
  );

  const handleToggleSettingActive = useCallback(
    async (id: string, currentActive: boolean) => {
      await updateSetting.mutateAsync({ id, updates: { isActive: !currentActive } });
    },
    [updateSetting],
  );

  // ─── List Handlers ─────────────────────────────────────────────

  const handleAddList = useCallback(() => {
    setAdding(true);
    setEditingId(null);
    setListForm(emptyListForm);
  }, []);

  const handleEditList = useCallback(
    (item: {
      id: string;
      listCode: string;
      name: string;
      description: string | null;
      itemType: string;
      items: unknown[];
    }) => {
      setEditingId(item.id);
      setAdding(false);
      setListForm({
        listCode: item.listCode,
        name: item.name,
        description: item.description || '',
        itemType: item.itemType as AppSettingListItemType,
        items: JSON.stringify(item.items, null, 2),
      });
    },
    [],
  );

  const handleSaveNewList = useCallback(async () => {
    let parsedItems: unknown[] = [];
    try {
      parsedItems = JSON.parse(listForm.items);
    } catch {
      // default to empty array
    }
    await createList.mutateAsync({
      listCode: listForm.listCode,
      name: listForm.name,
      itemType: listForm.itemType,
      ...(listForm.description && { description: listForm.description }),
      items: parsedItems,
    });
    setAdding(false);
    setListForm(emptyListForm);
  }, [createList, listForm]);

  const handleSaveEditList = useCallback(async () => {
    if (!editingId) return;
    let parsedItems: unknown[] = [];
    try {
      parsedItems = JSON.parse(listForm.items);
    } catch {
      // default to empty array
    }
    await updateList.mutateAsync({
      id: editingId,
      updates: {
        name: listForm.name,
        description: listForm.description || null,
        itemType: listForm.itemType,
        items: parsedItems,
      },
    });
    setEditingId(null);
  }, [editingId, updateList, listForm]);

  const handleDeleteList = useCallback(
    async (id: string) => {
      await deleteList.mutateAsync(id);
    },
    [deleteList],
  );

  const handleCancel = useCallback(() => {
    setAdding(false);
    setEditingId(null);
    setSettingForm(emptySettingForm);
    setListForm(emptyListForm);
  }, []);

  const handleSettingValueChange = useCallback((val: string | string[]) => {
    const stringVal = Array.isArray(val) ? JSON.stringify(val) : val;
    setSettingForm((f) => ({ ...f, value: stringVal }));
  }, []);

  if (settingsLoading || listsLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 size={24} className="animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div>
      {/* Sub-tabs */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <div className="flex gap-2">
          <button
            onClick={() => {
              setView('settings');
              handleCancel();
            }}
            className={`px-3 py-1.5 text-sm rounded-md ${
              view === 'settings'
                ? 'bg-primary-100 text-primary-700 font-medium'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Settings
          </button>
          <button
            onClick={() => {
              setView('lists');
              handleCancel();
            }}
            className={`px-3 py-1.5 text-sm rounded-md ${
              view === 'lists'
                ? 'bg-primary-100 text-primary-700 font-medium'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Lists
          </button>
        </div>
        <button
          onClick={view === 'settings' ? handleAddSetting : handleAddList}
          disabled={adding}
          className="flex items-center gap-1 px-3 py-1.5 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
        >
          <Plus size={14} />
          Add
        </button>
      </div>

      {view === 'settings' && <AiModelsSection settings={settings} />}

      {view === 'settings' ? (
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Var Code
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Name
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Type
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Value
              </th>
              <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                Active
              </th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {adding && (
              <tr className="border-b border-gray-100 bg-blue-50">
                <td className="px-4 py-2">
                  <input
                    value={settingForm.varCode}
                    onChange={(e) => setSettingForm((f) => ({ ...f, varCode: e.target.value }))}
                    placeholder="VAR_CODE"
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                    data-testid="new-varcode-input"
                  />
                </td>
                <td className="px-4 py-2">
                  <input
                    value={settingForm.name}
                    onChange={(e) => setSettingForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="Name"
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                    data-testid="new-name-input"
                  />
                </td>
                <td className="px-4 py-2">
                  <select
                    value={settingForm.varType}
                    onChange={(e) =>
                      setSettingForm((f) => ({
                        ...f,
                        varType: e.target.value as AppSettingVarType,
                      }))
                    }
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  >
                    {VAR_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-2">
                  <SettingValueInput
                    varType={settingForm.varType}
                    value={settingForm.value}
                    onChange={handleSettingValueChange}
                  />
                </td>
                <td />
                <td className="px-4 py-2 text-right">
                  <button
                    onClick={handleSaveNewSetting}
                    disabled={
                      !settingForm.varCode.trim() ||
                      !settingForm.name.trim() ||
                      createSetting.isPending
                    }
                    className="text-green-600 hover:text-green-800 p-1 disabled:opacity-50"
                    aria-label="Save new item"
                  >
                    <Check size={16} />
                  </button>
                  <button
                    onClick={handleCancel}
                    className="text-gray-400 hover:text-gray-600 p-1 ml-1"
                    aria-label="Cancel"
                  >
                    <X size={16} />
                  </button>
                </td>
              </tr>
            )}
            {settings.map((item) => (
              <tr key={item.id} className="border-b border-gray-100">
                {editingId === item.id ? (
                  <>
                    <td className="px-4 py-2 text-gray-600">{item.varCode}</td>
                    <td className="px-4 py-2">
                      <input
                        value={settingForm.name}
                        onChange={(e) => setSettingForm((f) => ({ ...f, name: e.target.value }))}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                        data-testid="edit-name-input"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <select
                        value={settingForm.varType}
                        onChange={(e) =>
                          setSettingForm((f) => ({
                            ...f,
                            varType: e.target.value as AppSettingVarType,
                          }))
                        }
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                      >
                        {VAR_TYPES.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2">
                      <SettingValueInput
                        varType={settingForm.varType}
                        value={settingForm.value}
                        onChange={handleSettingValueChange}
                      />
                    </td>
                    <td />
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={handleSaveEditSetting}
                        disabled={!settingForm.name.trim() || updateSetting.isPending}
                        className="text-green-600 hover:text-green-800 p-1 disabled:opacity-50"
                        aria-label="Save edit"
                      >
                        <Check size={16} />
                      </button>
                      <button
                        onClick={handleCancel}
                        className="text-gray-400 hover:text-gray-600 p-1 ml-1"
                        aria-label="Cancel"
                      >
                        <X size={16} />
                      </button>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-4 py-2 text-gray-900 font-mono text-xs">{item.varCode}</td>
                    <td className="px-4 py-2 text-gray-900">{item.name}</td>
                    <td className="px-4 py-2">
                      <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">
                        {item.varType}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-gray-600 text-xs max-w-[200px] truncate">
                      {item.value || '-'}
                    </td>
                    <td className="px-4 py-2 text-center">
                      <button
                        onClick={() => handleToggleSettingActive(item.id, item.isActive)}
                        className={`inline-block w-8 h-4 rounded-full relative transition-colors ${
                          item.isActive ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                        aria-label={item.isActive ? 'Deactivate' : 'Activate'}
                      >
                        <span
                          className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-transform ${
                            item.isActive ? 'left-4' : 'left-0.5'
                          }`}
                        />
                      </button>
                    </td>
                    <td className="px-4 py-2 text-right flex items-center justify-end gap-1">
                      <button
                        onClick={() => handleEditSetting(item)}
                        className="text-gray-400 hover:text-primary-600 p-1"
                        aria-label={`Edit ${item.name}`}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => handleDeleteSetting(item.id)}
                        className="text-gray-400 hover:text-red-600 p-1"
                        aria-label={`Delete ${item.name}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                List Code
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Name
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Item Type
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Items
              </th>
              <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                Active
              </th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {adding && (
              <tr className="border-b border-gray-100 bg-blue-50">
                <td className="px-4 py-2">
                  <input
                    value={listForm.listCode}
                    onChange={(e) => setListForm((f) => ({ ...f, listCode: e.target.value }))}
                    placeholder="LIST_CODE"
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                    data-testid="new-listcode-input"
                  />
                </td>
                <td className="px-4 py-2">
                  <input
                    value={listForm.name}
                    onChange={(e) => setListForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="Name"
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                    data-testid="new-list-name-input"
                  />
                </td>
                <td className="px-4 py-2">
                  <select
                    value={listForm.itemType}
                    onChange={(e) =>
                      setListForm((f) => ({
                        ...f,
                        itemType: e.target.value as AppSettingListItemType,
                      }))
                    }
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  >
                    {LIST_ITEM_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-2">
                  <textarea
                    value={listForm.items}
                    onChange={(e) => setListForm((f) => ({ ...f, items: e.target.value }))}
                    placeholder='["item1", "item2"]'
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono"
                    rows={2}
                  />
                </td>
                <td />
                <td className="px-4 py-2 text-right">
                  <button
                    onClick={handleSaveNewList}
                    disabled={
                      !listForm.listCode.trim() || !listForm.name.trim() || createList.isPending
                    }
                    className="text-green-600 hover:text-green-800 p-1 disabled:opacity-50"
                    aria-label="Save new item"
                  >
                    <Check size={16} />
                  </button>
                  <button
                    onClick={handleCancel}
                    className="text-gray-400 hover:text-gray-600 p-1 ml-1"
                    aria-label="Cancel"
                  >
                    <X size={16} />
                  </button>
                </td>
              </tr>
            )}
            {lists.map((item) => (
              <tr key={item.id} className="border-b border-gray-100">
                {editingId === item.id ? (
                  <>
                    <td className="px-4 py-2 text-gray-600 font-mono text-xs">{item.listCode}</td>
                    <td className="px-4 py-2">
                      <input
                        value={listForm.name}
                        onChange={(e) => setListForm((f) => ({ ...f, name: e.target.value }))}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                        data-testid="edit-list-name-input"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <select
                        value={listForm.itemType}
                        onChange={(e) =>
                          setListForm((f) => ({
                            ...f,
                            itemType: e.target.value as AppSettingListItemType,
                          }))
                        }
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                      >
                        {LIST_ITEM_TYPES.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2">
                      <textarea
                        value={listForm.items}
                        onChange={(e) => setListForm((f) => ({ ...f, items: e.target.value }))}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono"
                        rows={3}
                      />
                    </td>
                    <td />
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={handleSaveEditList}
                        disabled={!listForm.name.trim() || updateList.isPending}
                        className="text-green-600 hover:text-green-800 p-1 disabled:opacity-50"
                        aria-label="Save edit"
                      >
                        <Check size={16} />
                      </button>
                      <button
                        onClick={handleCancel}
                        className="text-gray-400 hover:text-gray-600 p-1 ml-1"
                        aria-label="Cancel"
                      >
                        <X size={16} />
                      </button>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-4 py-2 text-gray-900 font-mono text-xs">{item.listCode}</td>
                    <td className="px-4 py-2 text-gray-900">{item.name}</td>
                    <td className="px-4 py-2">
                      <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">
                        {item.itemType}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-gray-600 text-xs max-w-[200px] truncate">
                      {Array.isArray(item.items) ? `${item.items.length} items` : '-'}
                    </td>
                    <td className="px-4 py-2 text-center">
                      <span
                        className={`inline-block w-2 h-2 rounded-full ${
                          item.isActive ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                      />
                    </td>
                    <td className="px-4 py-2 text-right flex items-center justify-end gap-1">
                      <button
                        onClick={() => handleEditList(item)}
                        className="text-gray-400 hover:text-primary-600 p-1"
                        aria-label={`Edit ${item.name}`}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => handleDeleteList(item.id)}
                        className="text-gray-400 hover:text-red-600 p-1"
                        aria-label={`Delete ${item.name}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Dynamic Value Input ────────────────────────────────────────────────────

interface SettingValueInputProps {
  varType: AppSettingVarType;
  value: string;
  onChange: (value: string | string[]) => void;
}

export function SettingValueInput({ varType, value, onChange }: SettingValueInputProps) {
  switch (varType) {
    case 'TEXT':
      return (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Text value"
          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
        />
      );
    case 'NUMBER':
      return (
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="0"
          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
        />
      );
    case 'DATE':
      return (
        <input
          type="date"
          value={value ? value.split('T')[0] : ''}
          onChange={(e) => onChange(e.target.value ? new Date(e.target.value).toISOString() : '')}
          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
        />
      );
    case 'BOOLEAN':
      return (
        <button
          type="button"
          onClick={() => onChange(value === 'true' ? 'false' : 'true')}
          className={`inline-block w-10 h-5 rounded-full relative transition-colors ${
            value === 'true' ? 'bg-green-500' : 'bg-gray-300'
          }`}
          aria-label={`Toggle ${value === 'true' ? 'off' : 'on'}`}
        >
          <span
            className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
              value === 'true' ? 'left-5' : 'left-0.5'
            }`}
          />
        </button>
      );
    case 'USER':
      return (
        <SearchableSelect
          entityType="user"
          value={value}
          onChange={(v) => onChange(v)}
          placeholder="Search user..."
        />
      );
    case 'VENDOR':
      return (
        <SearchableSelect
          entityType="vendor"
          value={value}
          onChange={(v) => onChange(v)}
          placeholder="Search vendor..."
        />
      );
    case 'VENUE':
      return (
        <SearchableSelect
          entityType="venue"
          value={value}
          onChange={(v) => onChange(v)}
          placeholder="Search venue..."
        />
      );
    case 'LIST':
      return (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder='["item1", "item2"]'
          className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono"
          rows={2}
        />
      );
    default:
      return (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
        />
      );
  }
}
